import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { Nav } from '@/components/layout'
import { Spinner } from '@/components/ui'
import { ErrorFallback } from '@/components/ErrorFallback'
import { useAuth } from '@/hooks/useAuth'
import { SubscriptionProvider } from '@/context/SubscriptionContext'
import { AccountProvider } from '@/context/AccountContext'
import {
  CreatePage,
  QueuePage,
  AnalyticsPage,
  VoicePage,
  SettingsPage,
  AccountsPage,
  LoginPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  TwitterCallbackPage,
  PlatformCallbackPage,
  LandingPage,
  PricingPage,
  CheckoutSuccessPage,
  CheckoutCancelPage,
  TermsPage,
  PrivacyPage,
  BlogPage,
} from '@/pages'

function LandingRoute() {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-bg">
        <Spinner size="lg" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/create" replace />
  }

  return <LandingPage />
}

function ProtectedLayout() {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-bg">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="min-h-screen bg-primary-bg">
      <Nav />
      <main>
        <Outlet />
      </main>
    </div>
  )
}

function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-bg">
        <Spinner size="lg" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/create" replace />
  }

  return <Outlet />
}

// Protected route without Nav - for transitional pages like OAuth callbacks
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-bg">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <Outlet />
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <SubscriptionProvider>
      <AccountProvider>
      <Routes>
      {/* Landing page - unauthenticated see marketing, authenticated redirect */}
      <Route path="/" element={<LandingRoute />} />

      {/* Public pages (no auth wrapper) */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />

      {/* Public routes (redirect if authenticated) */}
      <Route element={<PublicRoute />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Protected routes without Nav (transitional pages) */}
      <Route element={<ProtectedRoute />}>
        <Route path="/auth/twitter/callback" element={<TwitterCallbackPage />} />
        <Route path="/auth/:platform/callback" element={<PlatformCallbackPage />} />
      </Route>

      {/* Protected routes with Nav */}
      <Route element={<ProtectedLayout />}>
        <Route path="/create" element={<CreatePage />} />
        <Route path="/queue" element={<QueuePage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/voice" element={<VoicePage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </AccountProvider>
      </SubscriptionProvider>
    </Sentry.ErrorBoundary>
  )
}
